/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";

import { Params } from "react-router-dom";

import { DataType, FeatureType } from "@/jenesys-types/data/enums";
import { AppError, extractError } from "@/jenesys-types/errors";

import {
  PathParams,
  performDataAction,
  performFeatureAction
} from "@/services/api";

import useIsMounted from "./useIsMounted";

interface Options {
  enabled?: Boolean;
  refetchOnChange?: Boolean;
}

const useItemAction = <T = any>(
  actionName: string,
  pathParams: PathParams,
  dataType?: DataType,
  featureCluster?: FeatureType,
  params?: Params,
  data?: Record<string, any>,
  options?: Options
) => {
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AppError | undefined>();
  const [response, setResponse] = useState<T | undefined>();
  const [hasRequestedOnce, setHasRequestedOnce] = useState(false);

  const { enabled = true, refetchOnChange = false } = options || {};

  const hasResponse = response !== undefined;

  // Ref to track if the effect has already run
  const hasEffectRun = useRef(false);

  const performAction = useCallback(() => {
    setIsLoading(true);
    setError(undefined);
    setHasRequestedOnce(true);

    const responsePromise: Promise<T> = dataType
      ? performDataAction(dataType, actionName, pathParams, params, data)
      : performFeatureAction(
          featureCluster!,
          actionName,
          pathParams,
          params,
          data
        );

    responsePromise
      .then((res) => {
        if (isMounted()) {
          setResponse(res);
          setIsLoading(false);
        }
      })
      .catch((e) => {
        if (isMounted()) {
          setResponse(undefined);
          setError(extractError(e));
          setIsLoading(false);
        }
      });
  }, [data, isMounted, params, pathParams]);

  useEffect(() => {
    if (!enabled) return;

    // Prevent the effect from running multiple times in strict mode
    if (hasEffectRun.current && !refetchOnChange) {
      return;
    }
    hasEffectRun.current = true;

    // Don't fetch if no data type or feature cluster, or if not enabled
    if (!dataType && !featureCluster) {
      return;
    }

    // Skip additional fetches if we already have a response and don't want to refetch
    if (hasRequestedOnce && hasResponse && !refetchOnChange) {
      return;
    }

    performAction();
  }, [
    enabled,
    JSON.stringify(params),
    JSON.stringify(pathParams),
    data,
    isMounted,
    refetchOnChange,
    hasRequestedOnce,
    hasResponse
  ]);

  return {
    isLoading,
    error,
    response,
    refetch: performAction
  };
};

export default useItemAction;
